// src/components/canine-teeth/ImageSelector.js

import React, { useState, useRef, useEffect } from 'react';
import SubImageAnnotator from './SubImageAnnotator';
import ImageVerification from './ImageVerification';
import CategorySelector from './CategorySelector';
import { FaTrash} from 'react-icons/fa';
import * as UTIF from 'utif';


function ImageSelector({
  imageFile,
  userEmail = '',
  tipo = '',
  posizione = '',
  gender = '',
  side = '',
  realAge = '',
  selectionRequired = true,
  maxTotalPoints = 7,
  colorCategories = ['blue', 'red'],
  categoryColors = { blue: 'blue', red: 'red' },
  passCategoryToAnnotation = true,
  maxPointsPerCategory = {},
  useLeftRightInNames = false,
  categoriesToAnnotate = ['blue', 'red'],
  loadedSession = null,
  updateSessionsCount,
}) {
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [subImages, setSubImages] = useState([]);
  const [currentStep, setCurrentStep] = useState(
    selectionRequired ? 'selection' : 'annotation'
  );
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [loadedImage, setLoadedImage] = useState(null);
  const [category, setCategory] = useState(colorCategories[0]);
  const [mousePosition, setMousePosition] = useState(null);
  const [allAnnotations, setAllAnnotations] = useState({});
  const [setAnnotatedImagesData] = useState({});
  const imageRef = useRef(null);
  const canvasRef = useRef(null);

  const [isDraggingPoint, setIsDraggingPoint] = useState(false);
  const [draggingPointIndex, setDraggingPointIndex] = useState(null);
  const clickDisabled = useRef(false);

  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const minZoom = 0.5;
  const maxZoom = 5;


  useEffect(() => {
    if (loadedSession) {
      setSubImages([loadedSession.subImage]);
      setCurrentStep('annotation');
      setAllAnnotations({ [loadedSession.subImage.id]: loadedSession });
    }
  }, [loadedSession]);

  useEffect(() => {
    if (!imageFile) return;

    const fileType = imageFile.type;

    const loadImage = (dataURL) => {
      const img = new Image();
      img.onload = () => {
        setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
        setLoadedImage(img);

        if (!selectionRequired && !loadedSession) {
          const nameBase = imageFile.name.replace(/\.[^/.]+$/, '');

          const subImg = {
            id: 1,
            name: `${nameBase}.jpg`,
            dataURL: dataURL,
            center: { x: img.width / 2, y: img.height / 2 },
            category: passCategoryToAnnotation ? null : undefined,
            originalPosition: { x: 0, y: 0 },
            originalImageName: imageFile.name,
          };

          setSubImages([subImg]);
          setCurrentStep('annotation');
        }
      };
      img.src = dataURL;
    };

    if (fileType === 'image/tiff' || fileType === 'image/x-tiff') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const arrayBuffer = e.target.result;
        const ifds = UTIF.decode(arrayBuffer);
        UTIF.decodeImage(arrayBuffer, ifds[0]);
        const firstPage = ifds[0];
        const rgba = UTIF.toRGBA8(firstPage);
        const canvas = document.createElement('canvas');
        canvas.width = firstPage.width;
        canvas.height = firstPage.height;
        const ctx = canvas.getContext('2d');
        const imageData = ctx.createImageData(canvas.width, canvas.height);
        imageData.data.set(rgba);
        ctx.putImageData(imageData, 0, 0);
        loadImage(canvas.toDataURL());
      };
      reader.readAsArrayBuffer(imageFile);
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        loadImage(e.target.result);
      };
      reader.readAsDataURL(imageFile);
    }
  }, [imageFile, selectionRequired, passCategoryToAnnotation, loadedSession]);

  const handleCategoryChange = (newCategory) => {
    setCategory(newCategory);
  };

  const handleImageClick = (e) => {
    if (isDraggingPoint) return;
    if (clickDisabled.current) {
      clickDisabled.current = false;
      return;
    }

    const totalPoints = selectedPoints.length;
    if (totalPoints >= maxTotalPoints) {
      alert(`Select up to ${maxTotalPoints} points.`);
      return;
    }

    const pointsInCategory = selectedPoints.filter((p) => p.category === category)
      .length;
    const categoryMax = maxPointsPerCategory[category] || maxTotalPoints;

    if (pointsInCategory >= categoryMax) {
      alert(`Max number of ${category} points reached.`);
      return;
    }

    const img = imageRef.current;
    if (!img) return;

    const rect = img.getBoundingClientRect();
    const scaleX = imageSize.width / (img.clientWidth * zoomLevel);
    const scaleY = imageSize.height / (img.clientHeight * zoomLevel);

    const x = (e.clientX - rect.left - panOffset.x) * scaleX;
    const y = (e.clientY - rect.top - panOffset.y) * scaleY;

    setSelectedPoints((prevPoints) => [...prevPoints, { x, y, category }]);
  };

  const handleMouseDown = (e) => {
    const img = imageRef.current;
    if (!img) return;

    const rect = img.getBoundingClientRect();
    const scaleX = imageSize.width / (img.clientWidth * zoomLevel);
    const scaleY = imageSize.height / (img.clientHeight * zoomLevel);

    const x = (e.clientX - rect.left - panOffset.x) * scaleX;
    const y = (e.clientY - rect.top - panOffset.y) * scaleY;

    selectedPoints.forEach((point, index) => {
      const dx = x - point.x;
      const dy = y - point.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance <= 10) {
        setIsDraggingPoint(true);
        setDraggingPointIndex(index);
        setMousePosition(null);
        clickDisabled.current = false;
      }
    });
  };

  const handleMouseMove = (e) => {
    const img = imageRef.current;
    if (!img) return;

    const rect = img.getBoundingClientRect();
    const scaleX = imageSize.width / (img.clientWidth * zoomLevel);
    const scaleY = imageSize.height / (img.clientHeight * zoomLevel);

    const x = (e.clientX - rect.left - panOffset.x) * scaleX;
    const y = (e.clientY - rect.top - panOffset.y) * scaleY;

    let isOverPoint = false;

    selectedPoints.forEach((point) => {
      const dx = x - point.x;
      const dy = y - point.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance <= 10) {
        isOverPoint = true;
      }
    });

    const canvas = canvasRef.current;

    if (canvas) {
      if (isOverPoint || (isDraggingPoint && draggingPointIndex !== null)) {
        canvas.style.cursor = 'move';
      } else {
        canvas.style.cursor = 'crosshair';
      }
    }

    if (isDraggingPoint && draggingPointIndex !== null) {
      setSelectedPoints((prevPoints) => {
        const newPoints = [...prevPoints];
        newPoints[draggingPointIndex] = { ...newPoints[draggingPointIndex], x, y };
        return newPoints;
      });
      clickDisabled.current = true;
    } else {
      setMousePosition({ x, y });
    }
  };

  const handleMouseUp = () => {
    setIsDraggingPoint(false);
    setDraggingPointIndex(null);
  };

  const handleMouseLeave = () => {
    setMousePosition(null);
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.style.cursor = 'crosshair';
    }
  };

  const handleWheel = (e) => {
    e.preventDefault();

    const img = imageRef.current;
    if (!img) return;

    const rect = img.getBoundingClientRect();

    const x = e.clientX - rect.left - panOffset.x;
    const y = e.clientY - rect.top - panOffset.y;

    let newZoomLevel = zoomLevel - e.deltaY * 0.001;
    newZoomLevel = Math.min(Math.max(newZoomLevel, minZoom), maxZoom);

    setZoomLevel(newZoomLevel);
    setPanOffset({
      x: x - ((x - panOffset.x) / zoomLevel) * newZoomLevel,
      y: y - ((y - panOffset.y) / zoomLevel) * newZoomLevel,
    });
  };

  const handleUndoLastPoint = () => {
    if (selectedPoints.length === 0) {
      alert('Nothing to do');
      return;
    }
    setSelectedPoints((prevPoints) => prevPoints.slice(0, -1));
  };

  const handleResetPoints = () => {
    setSelectedPoints([]);
  };

  const handleVerification = () => {
    if (selectedPoints.length > 0) {
      setCurrentStep('verification');
    } else {
      alert('Please select one point');
    }
  };

  const handleConfirmSelection = () => {
    if (loadedImage) {
      createSubImages();
    } else {
      alert("Image loading... please wait");
    }
  };

  const handleAdjustSelection = () => {
    setCurrentStep('selection');
  };

  const createSubImages = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const pointsToAnnotate = selectedPoints.filter((point) =>
      categoriesToAnnotate.includes(point.category)
    );

    if (pointsToAnnotate.length === 0) {
      alert('Unable to create sub-images. Select one point at least');
      return;
    }

    const rectWidth = 250;
    const rectHeight = 350;

    let leftmostPoint = null;
    let rightmostPoint = null;

    if (useLeftRightInNames) {
      const sortedPoints = pointsToAnnotate.slice().sort((a, b) => a.x - b.x);
      leftmostPoint = sortedPoints[0];
      rightmostPoint = sortedPoints[sortedPoints.length - 1];
    }

    const newSubImages = pointsToAnnotate.map((point, index) => {
      let sx = Math.round(point.x - rectWidth / 2);
      let sy = Math.round(point.y - rectHeight / 2);
      const sWidth = rectWidth;
      const sHeight = rectHeight;

      if (sx < 0) sx = 0;
      if (sy < 0) sy = 0;
      if (sx + sWidth > imageSize.width) sx = imageSize.width - sWidth;
      if (sy + sHeight > imageSize.height) sy = imageSize.height - sHeight;

      canvas.width = rectWidth;
      canvas.height = rectHeight;

      ctx.drawImage(
        loadedImage,
        sx,
        sy,
        sWidth,
        sHeight,
        0,
        0,
        rectWidth,
        rectHeight
      );

      const dataURL = canvas.toDataURL('image/jpeg', 1);
      const nameBase = imageFile.name.replace(/\.[^/.]+$/, '');

      let pointLabel = '';
      if (useLeftRightInNames) {
        if (point === leftmostPoint) {
          pointLabel = '_L_';
        } else if (point === rightmostPoint) {
          pointLabel = '_R_';
        }
      }

      const subImageName = `${nameBase}${pointLabel}_cut_${index + 1}.jpg`;

      return {
        id: index + 1,
        name: subImageName,
        dataURL,
        center: { x: point.x, y: point.y },
        category: passCategoryToAnnotation ? point.category : undefined,
        originalPosition: { x: sx, y: sy },
        originalImageName: imageFile.name,
      };
    });

    setSubImages(newSubImages);
    setCurrentStep('annotation');
  };

  const collectAnnotations = (subImageId, annotations, annotatedImageDataURL) => {
    setAllAnnotations((prevAnnotations) => ({
      ...prevAnnotations,
      [subImageId]: annotations,
    }));

    setAnnotatedImagesData((prevData) => ({
      ...prevData,
      [subImageId]: annotatedImageDataURL,
    }));
  };


  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;

    if (canvas && img) {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const imgWidth = img.clientWidth;
      const imgHeight = img.clientHeight;

      canvas.width = imgWidth;
      canvas.height = imgHeight;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.save();

      ctx.translate(panOffset.x, panOffset.y);
      ctx.scale(zoomLevel, zoomLevel);

      ctx.drawImage(img, 0, 0, canvas.width / zoomLevel, canvas.height / zoomLevel);

      const scaleX = imgWidth / imageSize.width;
      const scaleY = imgHeight / imageSize.height;

      selectedPoints.forEach((point) => {
        ctx.fillStyle = categoryColors[point.category] || 'black';
        ctx.beginPath();
        ctx.arc(point.x * scaleX, point.y * scaleY, 5 / zoomLevel, 0, 2 * Math.PI);
        ctx.fill();

        if (categoriesToAnnotate.includes(point.category)) {
          ctx.strokeStyle = categoryColors[point.category] || 'black';
          ctx.lineWidth = 2 / zoomLevel;
          ctx.setLineDash([10 / zoomLevel, 5 / zoomLevel]);
          const rectWidth = 250 * scaleX;
          const rectHeight = 350 * scaleY;
          ctx.strokeRect(
            (point.x - 125) * scaleX,
            (point.y - 175) * scaleY,
            rectWidth,
            rectHeight
          );
          ctx.setLineDash([]);
        }
      });

      if (
        !isDraggingPoint &&
        mousePosition &&
        categoriesToAnnotate.includes(category)
      ) {
        const { x, y } = mousePosition;
        ctx.strokeStyle = categoryColors[category] || 'black';
        ctx.lineWidth = 2 / zoomLevel;
        ctx.setLineDash([10 / zoomLevel, 5 / zoomLevel]);
        const rectWidth = 250 * scaleX;
        const rectHeight = 350 * scaleY;
        ctx.strokeRect(
          (x - 125) * scaleX,
          (y - 175) * scaleY,
          rectWidth,
          rectHeight
        );
        ctx.setLineDash([]);
      }

      ctx.restore();
    }
  }, [
    selectedPoints,
    mousePosition,
    category,
    categoryColors,
    categoriesToAnnotate,
    imageSize,
    isDraggingPoint,
    zoomLevel,
    panOffset,
  ]);

  if (currentStep === 'selection') {
    if (imageSize.width === 0 || imageSize.height === 0) {
      return <div>Caricamento dell'immagine...</div>;
    }

    return (
      <div style={styles.selectionContainer}>
        <FaTrash
          onClick={handleUndoLastPoint}
          style={styles.trashIcon}
          title="Annulla Ultimo Punto"
        />

        <div>
          <h2 style={styles.stepTitle}>
            Step 4: Select up to {maxTotalPoints} points on the image
          </h2>
          <p style={styles.stepDescription}>
            Select the teeth to analyze. You can choose from the following categories:
            {colorCategories.map((cat) => (
            <span
              key={cat}
              style={{ color: categoryColors[cat], marginLeft: '5px' }}
            >
              {cat}
            </span>
          ))}
          </p>
          <CategorySelector
            onChange={handleCategoryChange}
            currentCategory={category}
            categories={colorCategories}
            categoryColors={categoryColors}
          />
          <div style={styles.imageWrapper}>
            <img
              src={loadedImage ? loadedImage.src : ''}
              alt="Uploaded"
              ref={imageRef}
              style={styles.mainImage}
            />
            <canvas
              ref={canvasRef}
              style={styles.mainCanvas}
              onClick={handleImageClick}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onWheel={handleWheel}
            />
          </div>
          <div style={styles.selectedPointsContainer}>
            <h3 style={styles.selectedPointsTitle}>Selected points:</h3>
            <ul style={styles.selectedPointsList}>
              {selectedPoints.map((point, index) => (
                <li
                  key={index}
                  style={{ color: categoryColors[point.category] || 'black' }}
                >
                  Punto {index + 1} ({point.category}): ({Math.round(point.x)},{' '}
                  {Math.round(point.y)})
                </li>
              ))}
            </ul>
            <p style={styles.pointsCount}>
              Totale Selected points: {selectedPoints.length} (Massimo{' '}
              {maxTotalPoints})
            </p>
          </div>
          <div style={styles.buttonsContainer}>
            <button
              onClick={handleUndoLastPoint}
              style={styles.undoButton}
              title="Annulla Ultimo Punto"
            >
              Annulla Ultimo Punto
            </button>
            <button onClick={handleResetPoints} style={styles.resetButton}>
              Reset Punti
            </button>
            {selectedPoints.length > 0 && (
              <button onClick={handleVerification} style={styles.verifyButton}>
                Verifica Selezione
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 'verification') {
    return (
      <ImageVerification
        imageFile={imageFile}
        selectedPoints={selectedPoints}
        onConfirm={handleConfirmSelection}
        onAdjust={handleAdjustSelection}
      />
    );
  }

  if (currentStep === 'annotation' && subImages.length > 0) {
    return (
      <div>
        {subImages.map((subImg) => (
          <SubImageAnnotator
            key={subImg.id}
            subImage={subImg}
            collectAnnotations={collectAnnotations}
            email={userEmail}
            tipo={tipo}
            posizione={posizione}
            gender={gender}
            side={side}
            realAge={realAge}
            initialAnnotations={allAnnotations[subImg.id]}
            updateSessionsCount={updateSessionsCount}
          />
        ))}

      </div>
    );
  }

  return null;
}

const styles = {
  selectionContainer: {
    position: 'relative',
    padding: '20px',
    maxWidth: '800px',
    width: '100%',
    margin: '0 auto',
    backgroundColor: '#2d2d2d',
    borderRadius: '8px',
    color: '#fff',
  },
  trashIcon: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    cursor: 'pointer',
    fontSize: '24px',
    color: '#ff4d4d',
    zIndex: 10,
  },
  stepTitle: {
    textAlign: 'center',
    color: '#fff',
    marginBottom: '10px',
  },
  stepDescription: {
    textAlign: 'center',
    color: '#ccc',
    marginBottom: '20px',
  },
  imageWrapper: {
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
    margin: '0 auto',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  mainImage: {
    display: 'block',
    cursor: 'crosshair',
    width: '100%',
    height: 'auto',
    pointerEvents: 'none',
  },
  mainCanvas: {
    position: 'absolute',
    top: 0,
    left: 0,
    borderRadius: '8px',
    pointerEvents: 'auto',
    width: '100%',
    height: '100%',
  },
  selectedPointsContainer: {
    marginTop: '20px',
    textAlign: 'center',
  },
  selectedPointsTitle: {
    color: '#fff',
    marginBottom: '10px',
  },
  selectedPointsList: {
    listStyleType: 'none',
    padding: 0,
    margin: '0 auto',
    maxWidth: '600px',
    textAlign: 'left',
  },
  pointsCount: {
    marginTop: '10px',
    color: '#ccc',
  },
  buttonsContainer: {
    marginTop: '20px',
    textAlign: 'center',
  },
  undoButton: {
    marginRight: '10px',
    padding: '10px 20px',
    cursor: 'pointer',
    backgroundColor: '#555',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontWeight: 'bold',
    color: '#fff',
  },
  resetButton: {
    marginRight: '10px',
    padding: '10px 20px',
    cursor: 'pointer',
    backgroundColor: '#555',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontWeight: 'bold',
    color: '#fff',
  },
  verifyButton: {
    padding: '10px 20px',
    cursor: 'pointer',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontWeight: 'bold',
  },
  annotationTitle: {
    textAlign: 'center',
    color: '#fff',
    marginBottom: '20px',
  },
  footerButton: {
    display: 'flex',
    alignItems: 'center',
    margin: '5px 10px',
    padding: '10px 20px',
    backgroundColor: '#2196F3',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  icon: {
    marginRight: '8px',
  },
};

export default ImageSelector;